interface BuildAddMealToPlanPromptParams {
  /** Display name of the meal that was picked. */
  mealName: string;
  /** Id of the meal the agent should add (its plan tool keys off this). */
  mealId: string;
  /** Local plan weekday selected for the add. */
  day: string;
  /** Local plan date selected for the add, formatted yyyy-MM-dd. */
  targetDate: string;
  calories?: number | null;
  protein?: number | null;
  carbs?: number | null;
  fat?: number | null;
}

/**
 * Builds the hidden prompt sent to the agent when the user taps "Add" in their
 * food library. The visible chat bubble reads "I would like to add <meal> to my
 * plan" while this structured context supplies the meal id, macros and current
 * local plan date. Any missing macro is omitted so the list never has dangling
 * separators.
 *
 * Example:
 *   The user wants to add this meal to their plan: Arla Skyr Natural Icelandic
 *   Style Yogurt 450g (meal_id: ST0001), 280 kcal / 45g protein / 12g carbs /
 *   0g fat. Target plan day: monday (target_date: 2026-07-15).
 */
export const buildAddMealToPlanPrompt = ({
  mealName,
  mealId,
  day,
  targetDate,
  calories,
  protein,
  carbs,
  fat,
}: BuildAddMealToPlanPromptParams): string => {
  const macros: string[] = [];
  if (calories != null) macros.push(`${calories} kcal`);
  if (protein != null) macros.push(`${protein}g protein`);
  if (carbs != null) macros.push(`${carbs}g carbs`);
  if (fat != null) macros.push(`${fat}g fat`);
  const macroPart = macros.length ? `${macros.join(" / ")}, ` : "";

  return (
    `The user wants to add this meal to their plan: ${mealName} (meal_id: ${mealId}), ` +
    `${macroPart}Target plan day: ${day.toLowerCase()} (target_date: ${targetDate}).`
  );
};
